/**
 * Utility functions for parsing and extracting location data from Google Maps API responses
 */

interface AddressComponent {
	types: string[];
	longText?: string;
	shortText?: string;
}

export interface ParsedAddressComponents {
	locality: string;
	city: string;
	postalCode: string;
	state?: string;
	country?: string;
	sublocality?: string;
	area?: string;
}

/**
 * Parse Google Maps address components into structured location data
 * @param components - Array of address components from Google Maps API
 * @returns Parsed location data with locality, city, postal code, etc.
 */
export function parseAddressComponents(components: AddressComponent[]): ParsedAddressComponents {
	const result: ParsedAddressComponents = {
		locality: "",
		city: "",
		postalCode: "",
		state: "",
		country: "",
		sublocality: "",
		area: "",
	};

	components.forEach(component => {
		if (!component.longText) return;

		const types = component.types;
		const value = component.longText;

		// Extract locality (sublocality levels in priority order)
		if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
			result.locality = value;
			result.sublocality = value;
		} else if (types.includes("sublocality_level_2") && !result.locality) {
			result.locality = value;
			result.sublocality = value;
		} else if (types.includes("sublocality_level_3") && !result.locality) {
			result.locality = value;
			result.sublocality = value;
		}

		// Extract city (avoid sublocality types)
		if (types.includes("locality") && !types.includes("sublocality")) {
			result.city = value;
		}

		// Extract administrative area (city fallback)
		if (types.includes("administrative_area_level_2") && !result.city) {
			result.city = value;
		}

		// Extract area/neighborhood
		if (types.includes("neighborhood")) {
			result.area = value;
		}

		// Extract postal code
		if (types.includes("postal_code")) {
			result.postalCode = value;
		}

		// Extract state
		if (types.includes("administrative_area_level_1")) {
			result.state = value;
		}

		// Extract country
		if (types.includes("country")) {
			result.country = value;
		}
	});

	return result;
}

/**
 * Extract specific component type from address components
 * @param components - Array of address components
 * @param type - The component type to extract (e.g., 'locality', 'postal_code')
 * @returns The value of the component or empty string if not found
 */
export function extractComponentByType(components: AddressComponent[], type: string): string {
	const component = components.find(c => c.types.includes(type));
	return component?.longText || component?.shortText || "";
}

/**
 * Extract multiple component types in priority order
 * Returns the first match found
 * @param components - Array of address components
 * @param types - Array of component types in priority order
 * @returns The value of the first matching component or empty string
 */
export function extractComponentByTypes(components: AddressComponent[], types: string[]): string {
	for (const type of types) {
		const value = extractComponentByType(components, type);
		if (value) return value;
	}
	return "";
}

/**
 * Check if a location name matches the locality from components
 * Useful for determining if a place is a project or just a locality
 * @param locationName - The name to check (e.g., from place.displayName)
 * @param components - Array of address components
 * @returns True if the name differs from the locality (likely a project/building)
 */
export function isProjectName(locationName: string, components: AddressComponent[]): boolean {
	const parsed = parseAddressComponents(components);
	return parsed.locality.trim() !== locationName.trim();
}

/**
 * Format a complete address from components
 * @param components - Array of address components
 * @param options - Formatting options
 * @returns Formatted address string
 */
export function formatAddressFromComponents(
	components: AddressComponent[],
	options: {
		includePostalCode?: boolean;
		includeCity?: boolean;
		includeState?: boolean;
		includeCountry?: boolean;
	} = {},
): string {
	const { includePostalCode = true, includeCity = true, includeState = true, includeCountry = false } = options;

	const parsed = parseAddressComponents(components);
	const parts: string[] = [];

	if (parsed.sublocality) parts.push(parsed.sublocality);
	if (includeCity && parsed.city) parts.push(parsed.city);
	if (includeState && parsed.state) parts.push(parsed.state);
	if (includePostalCode && parsed.postalCode) parts.push(parsed.postalCode);
	if (includeCountry && parsed.country) parts.push(parsed.country);

	return parts.filter(Boolean).join(", ");
}

/**
 * Get locality with fallback options
 * Tries sublocality first, then area, then city
 * @param components - Array of address components
 * @returns The best available locality value
 */
export function getLocalityWithFallback(components: AddressComponent[]): string {
	const parsed = parseAddressComponents(components);
	return parsed.locality || parsed.area || parsed.city || "";
}
